import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useApplyRenewalCouponMutation,
  type ApplyRenewalCouponMutation,
} from '@/generated/graphql';
import { useToast } from '@/hooks/use-toast';
import { mapRenewalCouponError } from '@/lib/billing/mapRenewalCouponError';
import { formatCents } from '@/lib/format/currency';

export interface RenewalCouponFieldProps {
  initialCode?: string | null;
  /** Apply `initialCode` once on mount. */
  autoApply?: boolean;
  defaultOpen?: boolean;
  disabled?: boolean;
  onApplied?: (result: ApplyRenewalCouponMutation['applyRenewalCoupon']) => void | Promise<void>;
  /** Called once after the auto-apply attempt, success or error. */
  onAutoApplySettled?: () => void;
  /** Called with true when any apply starts and false once it has finished. */
  onPendingChange?: (pending: boolean) => void;
}

export const RenewalCouponField = ({
  initialCode,
  autoApply,
  defaultOpen,
  disabled,
  onApplied,
  onAutoApplySettled,
  onPendingChange,
}: RenewalCouponFieldProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [applyRenewalCoupon] = useApplyRenewalCouponMutation();
  const [open, setOpen] = useState(!!defaultOpen || !!initialCode);
  const [code, setCode] = useState((initialCode ?? '').toUpperCase());
  const [pending, setPending] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const autoAppliedRef = useRef(false);
  const onAutoApplySettledRef = useRef(onAutoApplySettled);
  onAutoApplySettledRef.current = onAutoApplySettled;
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;

  const apply = async (rawCode: string) => {
    const trimmed = rawCode.trim().toUpperCase();
    if (!trimmed) return;
    setPending(true);
    onPendingChangeRef.current?.(true);
    setErrorKey(null);
    try {
      const { data } = await applyRenewalCoupon({ variables: { code: trimmed } });
      const result = data?.applyRenewalCoupon;
      if (!result) {
        setErrorKey('renewalCoupon.errors.generic');
        return;
      }
      // -1 cycles is a lifetime coupon: it discounts every renewal.
      const unlimited = result.cycles === -1;
      const amount = formatCents(Math.round(result.discount * 100), 'BRL');
      let description: string;
      if (result.type === 'percentage') {
        description = unlimited
          ? t('renewalCoupon.appliedPercentUnlimited', { discount: result.discount })
          : t('renewalCoupon.appliedPercent', { discount: result.discount, count: result.cycles });
      } else {
        description = unlimited
          ? t('renewalCoupon.appliedFlatUnlimited', { amount })
          : t('renewalCoupon.appliedFlat', { amount, count: result.cycles });
      }
      toast({
        title: t('renewalCoupon.appliedTitle', { code: result.code }),
        description,
      });
      await onApplied?.(result);
    } catch (error: unknown) {
      setErrorKey(mapRenewalCouponError(error instanceof Error ? error.message : ''));
    } finally {
      setPending(false);
      onPendingChangeRef.current?.(false);
    }
  };

  useEffect(() => {
    if (!autoApply || !initialCode || autoAppliedRef.current) return;
    autoAppliedRef.current = true;
    void apply(initialCode).finally(() => onAutoApplySettledRef.current?.());
    // apply is intentionally omitted: the ref guard makes this a one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApply, initialCode]);

  if (!open) {
    return (
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 text-sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <Ticket className="w-4 h-4 mr-1" />
        {t('renewalCoupon.toggle')}
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          placeholder={t('renewalCoupon.placeholder')}
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !pending && !disabled) void apply(code);
          }}
          disabled={pending || disabled}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => void apply(code)}
          disabled={!code.trim() || pending || disabled}
        >
          {pending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {pending ? t('renewalCoupon.applying') : t('renewalCoupon.apply')}
        </Button>
      </div>
      {errorKey && <p className="text-sm text-destructive">{t(errorKey)}</p>}
    </div>
  );
};
